import React, {useState, useEffect, useContext} from 'react';
import { useParams } from "react-router-dom";

import PayPal from "./PayPal";

import Loading from "../../components/Loading";
import SumupWidget from "../../components/SumupWidget";

import Data from "../../../repository";

import styles from '../../../styles/Tokens.module.scss';
import AppContext from "../../../context";
import { trackMetaEvent, storePendingPurchase } from '../../../utils/metaPixel';

const SUBSCRIPTION_INTERVALS = [
	{ label: 'Weekly', interval: 'week', intervalCount: 1 },
	{ label: 'Bi-weekly', interval: 'week', intervalCount: 2 },
	{ label: 'Monthly', interval: 'month', intervalCount: 1 },
	{ label: 'Quarterly', interval: 'month', intervalCount: 3 },
	{ label: 'Yearly', interval: 'year', intervalCount: 1 },
];

function Checkout() {
	const { id } = useParams();
	const context = useContext(AppContext);

	const [loading, setLoading] = useState(true);
	const [product, setProduct] = useState(null);
	const [code, setCode] = useState("");
	const [discountCode, setDiscountCode] = useState(null);
	const [discountLoading, setDiscountLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(null);
	const [purchaseType, setPurchaseType] = useState('one-time');
	const [selectedInterval, setSelectedInterval] = useState(SUBSCRIPTION_INTERVALS[2]); // Default to monthly
	const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
	const [sumupSetupId, setSumupSetupId] = useState(null);
	const [sumupTransactionId, setSumupTransactionId] = useState(null);
	const [sumupPolling, setSumupPolling] = useState(false);

	const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
	const isSumup = context.domain?.paymentProcessor === 'SumUp';
	const canSubscribe = product?.credits > 0;

	useEffect(async () => {
		try {
			const product = await Data.fetchPublicProduct(id);
			setProduct(product.data);
			setLoading(false);
		} catch(error){
			window.location.href = '/dashboard/tokens';
		}
	}, [])

	useEffect(() => {
		if (product && !canSubscribe && purchaseType === 'subscription') {
			setPurchaseType('one-time');
		}
	}, [product, canSubscribe, purchaseType]);

	const trackInitiateCheckout = () => {
		const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
		if (!pixelId || !product) return;
		const currency = context.domain?.metaCurrency || 'GBP';
		const value = discountCode
			? Number(((product.price / 100) - (product.price / 100) * ((discountCode.percentageOff || 0) / 100)).toFixed(2))
			: product.price / 100;
		const name = product.description?.trim() || `${product.credits} Tokens`;
		trackMetaEvent({ pixelId, eventName: 'InitiateCheckout', params: { content_type: 'tokens', content_ids: [product._id], content_name: name, value, currency } });
		storePendingPurchase('tokens', { value, name, currency, productId: product._id });
	};

	const applyCode = async () => {
		try {
			setDiscountLoading(true);
			const discountCode = await Data.checkDiscountCode(code);
			setDiscountCode(discountCode.data);
			setDiscountLoading(false);
		} catch(error){
			alert("Discount code provided is not valid.");
			setCode("");
			setDiscountLoading(false);
		}
	}

	const proceedToWorldpay = async () => {
		trackInitiateCheckout();
		try {
			const checkoutSession = await Data.createWorldpayCheckout(product._id, discountCode);
			window.location.href = checkoutSession.data.url;
		} catch(error){
			console.log(error);
			window.location.href = '/dashboard/tokens';
		}
	}

	const proceedToStripe = async () => {
		trackInitiateCheckout();
		try {
			const checkoutSession = await Data.createStripeCheckout(product._id, discountCode);
			window.location.href = checkoutSession.data.url;
		} catch(error){
			console.log(error);
			window.location.href = '/dashboard/tokens';
		}
	}

	const proceedToSumup = async () => {
		trackInitiateCheckout();
		try {
			setError(null);
			const checkoutSession = await Data.createSumupCheckout(product._id, discountCode);
			setSumupCheckoutId(checkoutSession.data.id);
			setSumupTransactionId(checkoutSession.data.transactionId);
			setSumupPolling(true);
		} catch (error) {
			console.log(error);
			setError(error.response?.data?.message || 'Failed to start checkout. Please try again.');
		}
	}

	const proceedToWorldpaySubscription = async () => {
		trackInitiateCheckout();
		try {
			setError(null);
			const result = await Data.createWorldpayCreditsSubscription(
				product._id,
				selectedInterval.interval,
				selectedInterval.intervalCount
			);
			window.location.href = result.data.url;
		} catch(error){
			console.log(error);
			const errorMessage = error.response?.data?.message || 'Failed to create subscription. Please try again.';
			setError(errorMessage);
		}
	}

	const proceedToSumupSubscription = async () => {
		trackInitiateCheckout();
		try {
			setError(null);
			const result = await Data.createSumupCreditsSubscription(
				product._id,
				selectedInterval.interval,
				selectedInterval.intervalCount
			);
			setSumupCheckoutId(result.data.id);
			setSumupSetupId(result.data.setupId);
			setSumupPolling(true);
		} catch (error) {
			console.log(error);
			const errorMessage = error.response?.data?.message || 'Failed to create subscription. Please try again.';
			setError(errorMessage);
		}
	}

	useEffect(() => {
		if (!isSumup || !sumupCheckoutId || !sumupPolling) return;

		let cancelled = false;
		const intervalId = setInterval(async () => {
			if (cancelled) return;
			try {
				if (purchaseType === 'subscription' && sumupSetupId) {
					const result = await Data.verifySumupSubscription(sumupSetupId);
					if (result?.status === 200 || result?.data?.subscription) {
						window.location.href = `/dashboard/tokens?paymentStatus=success&setupId=${sumupSetupId}`;
					}
				} else if (sumupTransactionId) {
					const result = await Data.verifySumupPayment(sumupTransactionId, sumupCheckoutId);
					if (result?.status === 200 || result?.data?.transaction) {
						window.location.href = `/dashboard/tokens?paymentStatus=success&transactionId=${sumupTransactionId}&checkoutId=${sumupCheckoutId}`;
					}
				}
			} catch (err) {
				// keep polling until the payment settles
			}
		}, 3000);

		const timeoutId = setTimeout(() => {
			if (!cancelled) {
				setSumupPolling(false);
				clearInterval(intervalId);
			}
		}, 180000);

		return () => {
			cancelled = true;
			clearInterval(intervalId);
			clearTimeout(timeoutId);
		};
	}, [isSumup, sumupCheckoutId, sumupPolling, purchaseType, sumupSetupId, sumupTransactionId]);

	if(loading) return <Loading button={false} />

	return (
		<section className={styles.checkout}>
			<h2>Purchase Tokens</h2>

			<div className={styles.summary}>
				<h2>
					{product.description?.trim()
						? `${product.description}${product.credits > 0 ? ` (${product.credits} ${product.credits > 1 ? "Tokens" : "Token"})` : ''}`
						: `${product.credits} ${product.credits > 1 ? "Tokens" : "Token"}`}
				</h2>

				{discountCode ?
					<p><strike>£{(product.price / 100).toFixed(2)}</strike> £{((product.price / 100) - (product.price / 100 ) * (discountCode.percentageOff / 100)).toFixed(2)}</p>
				:
					<p>£{(product.price / 100).toFixed(2)}</p>
				}
			</div>

			{success ?
				<p className={styles.success}>Your purchase was successful!</p>
				:
				<>
					{isSumup && sumupCheckoutId && (
						<SumupWidget
							checkoutId={sumupCheckoutId}
							onSuccess={async () => {
								try {
									if (purchaseType === 'subscription') {
										await Data.verifySumupSubscription(sumupSetupId);
										window.location.href = `/dashboard/tokens?paymentStatus=success&setupId=${sumupSetupId}`;
									} else {
										await Data.verifySumupPayment(sumupTransactionId, sumupCheckoutId);
										window.location.href = `/dashboard/tokens?paymentStatus=success&transactionId=${sumupTransactionId}&checkoutId=${sumupCheckoutId}`;
									}
								} catch (err) {
									setError('Payment verification failed. Please try again.');
								}
							}}
							onError={() => setError('Payment failed. Please try again.')}
						/>
					)}

					{isSumup && sumupCheckoutId ? null : (
						<>
					{error && (
						<p className={styles.error}>{error}</p>
					)}

					{canSubscribe && (isWorldpay || isSumup) && (
						<div className={styles.purchaseTypeSelector}>
							<label className={purchaseType === 'one-time' ? styles.selected : ''}>
								<input
									type="radio"
									name="purchaseType"
									value="one-time"
									checked={purchaseType === 'one-time'}
									onChange={() => setPurchaseType('one-time')}
								/>
								One-time Purchase
							</label>
							<label className={purchaseType === 'subscription' ? styles.selected : ''}>
								<input
									type="radio"
									name="purchaseType"
									value="subscription"
									checked={purchaseType === 'subscription'}
									onChange={() => setPurchaseType('subscription')}
								/>
								Recurring Subscription
							</label>
						</div>
					)}

					{canSubscribe && purchaseType === 'subscription' && (isWorldpay || isSumup) && (
						<div className={styles.intervalSelector}>
							<label>Billing Frequency:</label>
							<select
								value={SUBSCRIPTION_INTERVALS.findIndex(i =>
									i.interval === selectedInterval.interval &&
									i.intervalCount === selectedInterval.intervalCount
								)}
								onChange={(e) => setSelectedInterval(SUBSCRIPTION_INTERVALS[e.target.value])}
							>
								{SUBSCRIPTION_INTERVALS.map((interval, index) => (
									<option key={index} value={index}>{interval.label}</option>
								))}
							</select>
							<p className={styles.subscriptionNote}>
								You'll receive {product.credits} token{product.credits > 1 ? 's' : ''} every {selectedInterval.label.toLowerCase()} for £{(product.price / 100).toFixed(2)}.
							</p>
						</div>
					)}

					{purchaseType === 'one-time' && (
						<div className={styles.discountCode}>
							{discountCode ?
								<>
									<p>Discount Applied: {discountCode.name} ({discountCode.percentageOff}% Off)</p>
									<img src={require("../../../images/cross.svg").default} alt="Remove" onClick={() => {
										setCode("");
										setDiscountCode(null);
									}} />
								</>
							:
								<>
									<input type="text" placeholder="Discount Code" value={code} onChange={(e) => { return setCode(e.target.value) }} />
									<button onClick={applyCode} className="btn btnEmpty">Apply</button>
								</>
							}
						</div>
					)}

					{!discountLoading && (
						context.domain.paymentProcessor === 'Paypal' ?
							<PayPal product={product} setSuccess={setSuccess} discountCode={discountCode?.code} />
						: context.domain.paymentProcessor === 'Worldpay' ?
							purchaseType === 'subscription' ?
								<button type={'button'} onClick={proceedToWorldpaySubscription} className="btn btnPrimary btn w-full mb-4">Subscribe</button>
							:
								<button type={'button'} onClick={proceedToWorldpay} className="btn btnPrimary btn w-full mb-4">Proceed to Checkout</button>
						: context.domain.paymentProcessor === 'SumUp' ?
							purchaseType === 'subscription' ?
								<button type={'button'} onClick={proceedToSumupSubscription} className="btn btnPrimary btn w-full mb-4">Subscribe</button>
							:
								<button type={'button'} onClick={proceedToSumup} className="btn btnPrimary btn w-full mb-4">Proceed to Checkout</button>
						: context.domain.paymentProcessor === 'Stripe' ?
							<button type={'button'} onClick={proceedToStripe} className="btn btnPrimary btn w-full mb-4">Proceed to Checkout</button>
						:
							<p>No payment processor enabled.</p>
					)}

					<a href="/dashboard/tokens" className="btn btnEmpty">Cancel</a>
						</>
					)}
				</>
			}
		</section>
	);
}

export default Checkout;
